import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import CabiMascot from "../../components/Kids/CabiMascot";
import CabiBubble from "../../components/Kids/CabiBubble";
import Pill from "../../components/Kids/Pill";
import EmptyState from "../../components/Kids/EmptyState";
import useEntrySound from "../../hooks/useEntrySound";
import useIdleNudge from "../../hooks/useIdleNudge";

const API       = process.env.REACT_APP_API_URL;
const AI_WS_URL = process.env.REACT_APP_AI_WS_URL || "wss://ai.cabil.co";

const QUICK_QUESTION_KEYS = ["q1", "q2", "q3", "q4", "q5"];

export default function KidsAIChat() {
  const { t } = useTranslation();
  const navigate  = useNavigate();
  const [params]  = useSearchParams();
  const token     = localStorage.getItem("kids_parent_token");

  const childId = params.get("childId") || sessionStorage.getItem("childId") || "guest";

  const [child, setChild] = useState({
    name:  sessionStorage.getItem("childName")  || "friend",
    grade: parseInt(sessionStorage.getItem("childGrade") || "1", 10),
  });
  const childName = child.name;
  const grade     = child.grade;

  useEntrySound(`Hi ${childName}! I'm Cabi. Ask me anything!`);
  useIdleNudge(childName);

  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [connected, setConnected] = useState(false);
  const [isTyping, setIsTyping]   = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [usage, setUsage] = useState(null);

  const wsRef      = useRef(null);
  const bottomRef  = useRef(null);
  const childRef   = useRef(child);
  childRef.current = child;

  useEffect(() => {
    if (!childId || childId === "guest" || !token) return;
    axios.get(`${API}/api/kids/child/${childId}/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      const c = res.data?.data;
      if (!c) return;
      setChild({ name: c.name, grade: c.grade ?? 1 });
      setAiEnabled(c.ai_enabled !== false);
      sessionStorage.setItem("childId",    c.id);
      sessionStorage.setItem("childName",  c.name);
      sessionStorage.setItem("childGrade", c.grade ?? 1);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  useEffect(() => {
    if (!token) { navigate("/kids/login"); return; }
    if (!aiEnabled) return;

    const ws = new WebSocket(`${AI_WS_URL}/ws/kids/${childId}`);
    wsRef.current = ws;
    let initDone = false;

    ws.onopen = () => {
      setConnected(true);
      const cc = childRef.current;
      ws.send(JSON.stringify({ type: "init", child_name: cc.name, grade: cc.grade }));
    };

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      setIsTyping(false);
      if (!initDone) { initDone = true; return; }
      setMessages(prev => [...prev, { role: "assistant", content: data.message, ts: Date.now() }]);
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEnabled]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!childId || childId === "guest" || !token) return;
    axios.get(`${API}/api/kids/ai/usage/${childId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUsage(r.data?.data || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const limitReached = usage && !usage.unlimited && (usage.remaining ?? 1) <= 0;
  const quickQuestions = QUICK_QUESTION_KEYS.map((k) => t(`kids.aiChat.${k}`));

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || !wsRef.current || wsRef.current.readyState !== 1) return;

    try {
      const r = await axios.post(`${API}/api/kids/ai/usage/${childId}/consume`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const u = r.data?.data;
      if (u) {
        setUsage({ used: u.used, limit: u.limit, remaining: u.remaining, unlimited: u.unlimited });
        if (u.allowed === false) {
          setMessages(prev => [...prev, { role: "assistant", content: "You've used all your Cabi chats for now. Ask a grown-up to add more!", ts: Date.now() }]);
          setInput("");
          return;
        }
      }
    } catch { /* network hiccup — don't block the child */ }

    setMessages(prev => [...prev, { role: "user", content: msg, ts: Date.now() }]);
    setIsTyping(true);
    wsRef.current.send(JSON.stringify({ type: "message", message: msg, child_name: childName, grade }));
    setInput("");
  };

  if (!aiEnabled) return (
    <div className="flex items-center justify-center p-6 text-center min-h-[100dvh]" style={{ background: "#FEFBF7" }}>
      <div className="bg-white rounded-card-lg max-w-[360px] w-full shadow-sheet">
        <EmptyState
          title={t("kids.aiChat.notInPlan")}
          subtitle={t("kids.aiChat.notInPlanDesc")}
          cta={t("kids.aiChat.backToGames")}
          onCta={() => navigate("/kids/games?childId=" + childId)}
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-[100dvh]" style={{ background: "#FEFBF7" }}>

      {/* ── Header: Cabi avatar + online status + usage meter (aiaccent) ── */}
      <header className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white border-b border-[#F5EDE4]">
        <button onClick={() => navigate(-1)}
          className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center text-base font-extrabold bg-[#FFF5EE] text-brand">
          ←
        </button>
        <CabiMascot mood="happy" size={38} />
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-base text-ink">Cabi</div>
          <div className="text-[11px] font-bold" style={{ color: connected ? "#2FB67C" : "#B0A090" }}>
            {connected ? `● ${t("kids.aiChat.online")}` : t("kids.aiChat.connecting")}
          </div>
        </div>
        {usage && (
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-[10px] font-extrabold whitespace-nowrap"
              style={{ color: limitReached ? "#D14343" : "#7C5CFF" }}>
              {usage.unlimited ? t("kids.aiChat.used", { count: usage.used }) : `${usage.used}/${usage.limit}`}
            </span>
            {!usage.unlimited && (
              <div className="w-20 h-1.5 rounded-full bg-[#F0E3D6] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, usage.limit > 0 ? (usage.used / usage.limit) * 100 : 0)}%`,
                    background: limitReached ? "#E5484D" : (usage.used / usage.limit) > 0.8 ? "#FBC531" : "#7C5CFF",
                  }} />
              </div>
            )}
            {usage.resets_in && (
              <span className="text-[8px] text-faint font-semibold">Resets {usage.resets_in}</span>
            )}
          </div>
        )}
      </header>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-4 py-5 max-w-[520px] mx-auto w-full">
        {messages.length === 0 && (
          <div className="text-center pt-10">
            <div className="flex justify-center mb-3">
              <CabiMascot mood="waving" size={80} />
            </div>
            <p className="font-display font-semibold text-[17px] text-ink mb-1">{t("kids.aiChat.greeting", { name: childName })}</p>
            <p className="text-[13px] text-muted mb-5">{t("kids.aiChat.whatToLearn")}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {quickQuestions.map((q) => (
                <Pill key={q} onClick={() => send(q)} className="hover:!border-aiaccent">
                  {q}
                </Pill>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          m.role === "user" ? (
            <div key={i} className="flex justify-end mb-3">
              <div className="bg-brand text-white font-semibold text-sm px-3.5 py-2.5 max-w-[78%] leading-snug rounded-2xl rounded-ee-md">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="mb-3">
              <CabiBubble text={m.content} side="start" theme="light" mood="happy" size={32} />
            </div>
          )
        ))}

        {isTyping && (
          <div className="flex gap-2 items-end mb-3">
            <div className="shrink-0"><CabiMascot mood="thinking" size={32} /></div>
            <div className="bg-white px-4 py-3 flex gap-1.5 rounded-2xl rounded-ss-md border border-cardline">
              {[0, 1, 2].map(j => (
                <span key={j} className="w-1.5 h-1.5 rounded-full inline-block"
                  style={{ background: "#7C5CFF", animation: "cabiDot .8s ease-in-out infinite", animationDelay: `${j * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="px-3.5 pb-3.5 pt-2 max-w-[520px] mx-auto w-full">
        <div className="flex gap-2 items-center bg-white ps-3.5 py-1.5 pe-1.5 rounded-2xl border-[1.5px] border-[#F0E3D6] focus-within:border-aiaccent">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder={limitReached ? t("kids.aiChat.limitReached") : t("kids.aiChat.placeholder")}
            disabled={limitReached}
            className="flex-1 text-sm font-semibold outline-none bg-transparent text-ink border-0"
          />
          <button onClick={() => send()} disabled={!input.trim() || !connected || limitReached}
            className="w-9 h-9 rounded-xl text-white text-base font-extrabold flex items-center justify-center transition-opacity"
            style={{ background: "#7C5CFF", opacity: (!input.trim() || !connected || limitReached) ? 0.4 : 1 }}>
            ➤
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cabiDot{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}
      `}</style>
    </div>
  );
}
